import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

export type SchoolOnboardingFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'date'
  | 'number'
  | 'checkbox'
  | 'single_select'
  | 'multi_select'
  | 'document_upload'
  | 'address'
  | 'rows'

export interface SchoolOnboardingFieldOption {
  label: string
  value: string
}

export interface SchoolOnboardingFormField {
  id: string
  label: string
  type: SchoolOnboardingFieldType
  required?: boolean
  placeholder?: string
  helpText?: string
  options?: SchoolOnboardingFieldOption[]
  columns?: Array<{ id: string; label: string }>
}

export interface SchoolOnboardingFormDefinition {
  fields: SchoolOnboardingFormField[]
}

export interface SchoolOnboardingTaskSummary {
  id: string
  /** Default chef document form, available without enrolling the kitchen in onboarding. */
  documentCollection?: boolean
  organizationId: string
  taskKey: string | null
  /** Campus this instance asks about (per-campus tasks); null for org-scoped tasks. */
  locationId: string | null
  locationName: string | null
  source: 'SYSTEM' | 'CUSTOM'
  category: string
  title: string
  description: string | null
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED'
  dueAt: string | null
  sortOrder: number
  formDefinition: SchoolOnboardingFormDefinition
  response: Record<string, unknown> | null
  completedAt: string | null
  completedByUserId: string | null
  /** Completer attribution: name + which side of the table completed it. */
  completedBy: { id: string; name: string; team: 'SCHOOL' | 'ORDO' } | null
  canceledAt: string | null
  createdAt: string
  updatedAt: string
  /**
   * Only present on a submit response: true when THIS submit finished the
   * org's last open task. Lets the task page route the school to the gate
   * home (waiting screen) instead of back to the task list.
   */
  becameComplete?: boolean
}

export interface SchoolOnboardingTaskResponse {
  task: SchoolOnboardingTaskSummary
}

export interface SchoolOnboardingDocumentUpload {
  fieldId: string
  title: string
  fileName: string
  contentType: string | null
  contentBase64: string
}

export async function fetchSchoolOnboardingTask(
  taskId: string,
  signal?: AbortSignal,
): Promise<SchoolOnboardingTaskResponse> {
  return apiFetch<SchoolOnboardingTaskResponse>(`/api/school-onboarding/tasks/${encodeURIComponent(taskId)}`, {
    signal,
  })
}

export async function submitSchoolOnboardingTask(input: {
  taskId: string
  response: Record<string, unknown>
  documents?: SchoolOnboardingDocumentUpload[]
}): Promise<SchoolOnboardingTaskResponse> {
  return apiFetch<SchoolOnboardingTaskResponse>(
    `/api/school-onboarding/tasks/${encodeURIComponent(input.taskId)}/submit`,
    {
      method: 'POST',
      body: {
        response: input.response,
        documents: input.documents ?? [],
      },
    },
  )
}

export function useSchoolOnboardingTaskQuery(taskId: string | null | undefined) {
  return useQuery<SchoolOnboardingTaskResponse>({
    queryKey: ['school-onboarding', 'task', taskId],
    queryFn: ({ signal }) => fetchSchoolOnboardingTask(taskId ?? '', signal),
    enabled: Boolean(taskId),
  })
}

export function useSubmitSchoolOnboardingTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: submitSchoolOnboardingTask,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-onboarding'] })
      void qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export interface SchoolOnboardingTasksResponse {
  tasks: SchoolOnboardingTaskSummary[]
}

/** Full onboarding checklist (open + completed) across the actor's school
 *  orgs — the `/onboarding` wizard's step rail. Already in checklist order
 *  (`sortOrder`), so consumers don't re-sort. */
export async function fetchSchoolOnboardingTasks(signal?: AbortSignal): Promise<SchoolOnboardingTasksResponse> {
  return apiFetch<SchoolOnboardingTasksResponse>('/api/school-onboarding/tasks', { signal })
}

export function useSchoolOnboardingTasksQuery() {
  return useQuery<SchoolOnboardingTasksResponse>({
    queryKey: ['school-onboarding', 'tasks'],
    queryFn: ({ signal }) => fetchSchoolOnboardingTasks(signal),
  })
}

/**
 * The wizard's landing step: the first task still awaiting a response, or —
 * when everything is done (review mode) — the first task. Null when the list
 * is empty. Pure — unit-tested.
 */
export function firstWizardTaskId(
  tasks: ReadonlyArray<Pick<SchoolOnboardingTaskSummary, 'id' | 'status'>>,
): string | null {
  const incomplete = tasks.find((task) => task.status !== 'COMPLETED')
  return incomplete?.id ?? tasks[0]?.id ?? null
}

/** The step to advance to after submitting `submittedId`: the next incomplete
 *  task after it in checklist order, else the first incomplete one before it,
 *  else null (checklist finished). Pure — unit-tested. */
export function nextWizardTaskId(
  tasks: ReadonlyArray<Pick<SchoolOnboardingTaskSummary, 'id' | 'status'>>,
  submittedId: string,
): string | null {
  const index = tasks.findIndex((task) => task.id === submittedId)
  const reordered = index >= 0 ? [...tasks.slice(index + 1), ...tasks.slice(0, index)] : tasks
  return reordered.find((task) => task.status !== 'COMPLETED' && task.id !== submittedId)?.id ?? null
}

export interface SchoolOnboardingTaskCommentAuthor {
  id: string
  name: string
  avatarUri: string | null
}

export interface SchoolOnboardingCommentMention {
  kind: 'user'
  entityId: string
  offset: number
  length: number
  displayName: string
}

export interface SchoolOnboardingTaskComment {
  id: string
  taskId: string
  body: string
  createdAt: string
  author: SchoolOnboardingTaskCommentAuthor | null
  mentions: SchoolOnboardingCommentMention[]
}

export interface SchoolOnboardingMentionCandidate {
  id: string
  kind: 'user'
  label: string
  subtitle: string | null
}

export interface SchoolOnboardingTaskCommentsResponse {
  comments: SchoolOnboardingTaskComment[]
}

export async function fetchSchoolOnboardingTaskComments(
  taskId: string,
  signal?: AbortSignal,
): Promise<SchoolOnboardingTaskCommentsResponse> {
  return apiFetch<SchoolOnboardingTaskCommentsResponse>(
    `/api/school-onboarding/tasks/${encodeURIComponent(taskId)}/comments`,
    { signal },
  )
}

export async function addSchoolOnboardingTaskComment(input: {
  taskId: string
  body: string
  mentions?: Array<{ kind: string; entityId: string; offset: number; length: number; displayName: string }>
}): Promise<{ comment: SchoolOnboardingTaskComment }> {
  return apiFetch<{ comment: SchoolOnboardingTaskComment }>(
    `/api/school-onboarding/tasks/${encodeURIComponent(input.taskId)}/comments`,
    {
      method: 'POST',
      body: { body: input.body, mentions: input.mentions ?? [] },
    },
  )
}

/** @mention suggestions for a task's comment composer. */
export async function fetchSchoolOnboardingMentionCandidates(
  taskId: string,
  query: string,
): Promise<{ candidates: SchoolOnboardingMentionCandidate[] }> {
  const params = new URLSearchParams({ q: query })
  return apiFetch<{ candidates: SchoolOnboardingMentionCandidate[] }>(
    `/api/school-onboarding/tasks/${encodeURIComponent(taskId)}/mention-candidates?${params.toString()}`,
  )
}

export function useSchoolOnboardingTaskCommentsQuery(taskId: string | null | undefined, enabled = true) {
  return useQuery<SchoolOnboardingTaskCommentsResponse>({
    queryKey: ['school-onboarding', 'task', taskId, 'comments'],
    queryFn: ({ signal }) => fetchSchoolOnboardingTaskComments(taskId ?? '', signal),
    enabled: Boolean(taskId) && enabled,
  })
}

export function useAddSchoolOnboardingTaskCommentMutation(taskId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      mentions?: Array<{ kind: string; entityId: string; offset: number; length: number; displayName: string }>
    }) => addSchoolOnboardingTaskComment({ taskId: taskId ?? '', body: input.body, mentions: input.mentions }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-onboarding', 'task', taskId, 'comments'] })
    },
  })
}

/**
 * A comment surfaced in the school admin's notification panel: the comment plus
 * the task/org it belongs to, so the row can deep-link to `/onboarding/:taskId`.
 */
export interface SchoolOnboardingCommentInboxItem extends SchoolOnboardingTaskComment {
  taskTitle: string
  taskKey: string | null
  organizationId: string
  organizationName: string
}

export interface SchoolOnboardingCommentInboxResponse {
  comments: SchoolOnboardingCommentInboxItem[]
}

export async function fetchSchoolOnboardingCommentInbox(
  signal?: AbortSignal,
): Promise<SchoolOnboardingCommentInboxResponse> {
  return apiFetch<SchoolOnboardingCommentInboxResponse>('/api/school-onboarding/comments', { signal })
}

/**
 * Aggregated onboarding comments across the signed-in school admin's tasks,
 * newest first. Feeds the topbar notification panel; polls on the same 30s
 * cadence as the tasks feed.
 */
export function useSchoolOnboardingCommentInboxQuery() {
  return useQuery<SchoolOnboardingCommentInboxResponse>({
    queryKey: ['school-onboarding', 'comment-inbox'],
    queryFn: ({ signal }) => fetchSchoolOnboardingCommentInbox(signal),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

/** Per-org onboarding rollup for the signed-in school admin. */
export interface SchoolOnboardingStatusSchool {
  organizationId: string
  organizationName: string
  deadline: string | null
  openTaskCount: number
  totalTaskCount: number
  isComplete: boolean
  /** ISO timestamp set when the Ordo team provisions the school's workspace. */
  provisionedAt: string | null
}

export interface SchoolOnboardingStatusResponse {
  schools: SchoolOnboardingStatusSchool[]
}

export async function fetchSchoolOnboardingStatus(signal?: AbortSignal): Promise<SchoolOnboardingStatusResponse> {
  return apiFetch<SchoolOnboardingStatusResponse>('/api/school-onboarding/status', { signal })
}

export function useSchoolOnboardingStatusQuery() {
  return useQuery<SchoolOnboardingStatusResponse>({
    queryKey: ['school-onboarding', 'status'],
    queryFn: ({ signal }) => fetchSchoolOnboardingStatus(signal),
    staleTime: 30_000,
  })
}

/**
 * True when the school's workspace should be gated. Live-gating audit GAP-0:
 * the gate keys off PROVISIONED STATUS ONLY — onboarding-task completion is no
 * longer a gate (the team can skip onboarding). A school is gated until the
 * Ordo team provisions it; once provisioned it's live, and reopening onboarding
 * tasks afterward (now advisory) does NOT re-gate it.
 */
export function isSchoolOnboardingGated(school: SchoolOnboardingStatusSchool): boolean {
  return !school.provisionedAt
}

/**
 * A gated school with nothing left to do (all tasks done, or no tasks at all)
 * is simply waiting on the Ordo team to provision → drives the "we're setting
 * you up" copy, distinct from the "finish your tasks" copy shown while tasks
 * are still open.
 */
export function isAwaitingProvisioning(school: SchoolOnboardingStatusSchool): boolean {
  return !school.provisionedAt && school.openTaskCount === 0
}

export interface SchoolOnboardingGate {
  loading: boolean
  gated: boolean
  school: SchoolOnboardingStatusSchool | null
  /** True when the gated school is complete and only waiting on provisioning. */
  awaitingProvisioning: boolean
}

/**
 * Resolves the home/sidebar onboarding gate: the first school that has started
 * onboarding but isn't provisioned yet, if any. Drives the gated home surface
 * and the disabled non-Tasks sidebar rows.
 */
export function useSchoolOnboardingGate(): SchoolOnboardingGate {
  const { data, isPending } = useSchoolOnboardingStatusQuery()
  const school = data?.schools.find(isSchoolOnboardingGated) ?? null
  return {
    loading: isPending,
    gated: Boolean(school),
    school,
    awaitingProvisioning: school ? isAwaitingProvisioning(school) : false,
  }
}

export interface HasSchoolOrgState {
  loading: boolean
  /** The signed-in user administers at least one school organization. True even
   *  before Ordo provisions a SCHOOL_CAMPUS location for that org. */
  hasSchoolOrg: boolean
  /** Display name of the active school (the sidebar-selected one, else the first). */
  schoolName: string | null
}

/**
 * Whether the user has a school org at all — independent of whether a campus
 * location has been provisioned yet. Lets school surfaces tell "your campus is
 * still being set up" apart from "you have no school" when `my-schools` is empty.
 */
export function useHasSchoolOrg(): HasSchoolOrgState {
  const { data, isPending } = useSchoolOnboardingStatusQuery()
  const schools = data?.schools ?? []
  const active = schools[0] ?? null
  return { loading: isPending, hasSchoolOrg: schools.length > 0, schoolName: active?.organizationName ?? null }
}
